import { useEffect, useState } from "react";
import { TableCell, TableRow } from "@mui/material";
import Table from "src/Components/Table";
import { GoldTaskPortfolio } from "src/Interfaces/goldArbitrage";
import { getGoldTaskPortfolio } from "src/Requests/goldArbitrage";
import { printPriceWithDecimalsForTableCell } from "src/helper";

const headers = ["ردیف", "اکانت", "نماد", "حجم", "آخرین قیمت", "ارزش", "% از پرتفو"];
const number = (value: number | null) =>
  value == null ? "—" : printPriceWithDecimalsForTableCell(value);

const Portfolio = ({ taskId }: { taskId?: number }) => {
  const [result, setResult] = useState<{
    taskId: number;
    data?: GoldTaskPortfolio;
    failed?: boolean;
  }>();

  useEffect(() => {
    if (!taskId) return;
    let disposed = false;
    let timeout: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    setResult({ taskId });

    const fetchPortfolio = async () => {
      try {
        const data = await getGoldTaskPortfolio(taskId, { signal: controller.signal });
        if (!disposed) setResult({ taskId, data });
      } catch {
        if (!disposed) setResult({ taskId, failed: true });
      } finally {
        if (!disposed) timeout = setTimeout(fetchPortfolio, 20000);
      }
    };
    fetchPortfolio();

    return () => {
      disposed = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [taskId]);

  const current = result?.taskId === taskId ? result : undefined;
  const data = current?.data;
  const rows = data ? [
    ...data.rows.filter(row => row.kind === "asset").sort((a, b) =>
      (b.portfolioPercent ?? -Infinity) - (a.portfolioPercent ?? -Infinity)),
    ...data.rows.filter(row => row.kind === "balance"),
    ...data.rows.filter(row => row.kind === "total"),
  ] : [];
  const message = !taskId
    ? "برای نمایش دارایی‌ها یک تسک را انتخاب کنید"
    : current?.failed
      ? "خطا در دریافت دارایی‌های اکانت"
      : !data
        ? "در حال دریافت دارایی‌ها…"
        : !data.hasPortfolio
          ? "اطلاعات پرتفو برای این اکانت موجود نیست"
          : undefined;

  return (
    <section className="w-full min-w-0 p-3" aria-label="دارایی‌های اکانت تسک">
      <div className="w-full m-auto border-2 border-sky-100 rounded overflow-auto max-h-[350px] order-table" dir="rtl">
        <Table
          header={<>{headers.map(header => (
            <TableCell key={header} className="bg-white hover:!bg-slate-200 !transition-all">
              <span className="!font-black text-black">{header}</span>
            </TableCell>
          ))}</>}
          body={message ? (
            <TableRow><TableCell colSpan={headers.length}><span role="status">{message}</span></TableCell></TableRow>
          ) : (
            <>{rows.map((row, index) => (
              <TableRow
                key={row.row}
                className={`!transition-all !w-full !relative ${row.kind === "total" ? "bg-slate-50" : "hover:!bg-slate-200"}`}
                sx={row.kind === "total" ? { "& .MuiTableCell-root": { fontWeight: 700 } } : undefined}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>{row.accountName}</TableCell>
                <TableCell>{row.symbol}</TableCell>
                <TableCell><span dir="ltr">{number(row.volume)}</span></TableCell>
                <TableCell><span dir="ltr">{number(row.lastPrice)}</span></TableCell>
                <TableCell><span dir="ltr">{number(row.value)}</span></TableCell>
                <TableCell><span dir="ltr">{row.portfolioPercent == null ? "—" : `${row.portfolioPercent.toFixed(2)}%`}</span></TableCell>
              </TableRow>
            ))}</>
          )}
        />
      </div>
    </section>
  );
};

export default Portfolio;
